import React, { useEffect } from "react"
import { useTracking } from "react-tracking"
import { IBannerComponent, ILogoComponent } from "@blackpearlmail/cerberus"

import { ColorPicker } from "../../../../../../components/color-picker/color-picker"
import ImageUpload from "../../../../../../components/image-upload/image-upload"
import { ImageSettings, updateImage, updateProfileImage, getImageTrackingLabel } from "./functions"
import Toggle from "../../../../../../components/toggle/toggle"
import { Image } from "../../../../../../components/image-upload/model"
import { BrandingImage, defaultBanner, defaultLogo, defaultProfile, IImageProps } from "./model"

import "./style.scss"

export const Images = (props: IImageProps) => {
    const { template, updateTemplate, images, callback, formik } = props
    const bottomBannerComponent = template.components[4] as IBannerComponent
    const topLogoComponent = template.components[0] as ILogoComponent

    const bannerImage = images.find((x) => x.type === "banner")?.image ?? {}
    const logoImage = images.find((x) => x.type === "logo")?.image ?? {}
    const profileImage = images.find((x) => x.type === "profile")?.image ?? {}

    const customBanner = {
        url: bannerImage.url,
        height: bannerImage.height,
        width: bannerImage.width,
        format: bannerImage.format,
    } as ImageSettings

    const customLogo = {
        url: logoImage.url,
        height: logoImage.height,
        width: logoImage.width,
        format: logoImage.format,
    } as ImageSettings

    const nullImage = {
        url: null,
        height: null,
        width: null,
        format: null,
    }

    const { trackEvent } = useTracking()

    const track = (trackingValue: string | undefined) => {
        trackEvent({
            event: "genericEvent",
            category: "click",
            action: "click image toggle",
            label: `${trackingValue}`,
        })
    }

    useEffect(() => {
        let localTemplate = template
        if (bottomBannerComponent.url !== null && images.find((x) => x.type === "banner")?.active) {
            localTemplate = updateImage(localTemplate, "bottom", bannerImage.url ? customBanner : defaultBanner)
        }
        if (topLogoComponent !== null && images.find((x) => x.type === "logo")?.active) {
            localTemplate = updateImage(localTemplate, "top", logoImage.url ? customLogo : defaultLogo)
        }
        if (images.find((x) => x.type === "profile")?.active) {
            formik.setFieldValue("profileimage", profileImage.url ?? formik.initialValues.profileimage)
            localTemplate = updateProfileImage(
                localTemplate,
                profileImage.width ?? defaultProfile.width,
                profileImage.height ?? defaultProfile.height
            )
        }
        updateTemplate(localTemplate)
    }, [images])

    function toggleTemplateImages(type: "logo" | "banner") {
        let selectedImage: ImageSettings
        if (type === "logo") {
            selectedImage = topLogoComponent.url !== null ? nullImage : logoImage.url ? customLogo : defaultLogo
        } else {
            selectedImage =
                bottomBannerComponent.url !== null ? nullImage : bannerImage.url ? customBanner : defaultBanner
        }
        updateTemplateImage(
            type,
            type === "logo" ? logoImage : bannerImage,
            !images.find((x) => x.type === type)?.active
        )
        updateTemplate(updateImage(template, type === "logo" ? "top" : "bottom", selectedImage))
    }

    function toggleLogo() {
        toggleTemplateImages("logo")
    }

    function toggleBanner() {
        toggleTemplateImages("banner")
    }

    function toggleProfile() {
        updateTemplateImage("profile", profileImage, !images.find((x) => x.type === "profile")?.active)
        formik.setFieldValue("profileimage", "", false)
    }

    const updateTemplateImage = (type: string, image: Image | null, active?: boolean) => {
        const update = images.map((img) => {
            if (img.type === type) {
                return {
                    type: type,
                    image: image,
                    active: active ?? true,
                } as BrandingImage
            }
            return img
        }) as BrandingImage[]

        callback(update)

        if (active !== undefined) {
            const trackingValue = getImageTrackingLabel(type, active)
            track(trackingValue)
        }
    }

    return (
        <div className="img">
            <div className="img-conversation">
                <p>Looking smart, here’s where the real magic happens...</p>
            </div>
            <div className="img-section">
                <h3>Logo</h3>
                <Toggle active={images.find((x) => x.type === "logo")?.active ?? true} callback={toggleLogo} />
                <ImageUpload
                    type="logo"
                    disabled={!images.find((x) => x.type === "logo")?.active ?? false}
                    callback={updateTemplateImage}
                    defaultImage={logoImage?.url ? logoImage : defaultLogo}
                />
            </div>
            <div className="img-section">
                <h3>Profile picture</h3>
                <Toggle active={images.find((x) => x.type === "profile")?.active ?? true} callback={toggleProfile} />
                <ImageUpload
                    type="profile"
                    disabled={!images.find((x) => x.type === "profile")?.active ?? false}
                    callback={updateTemplateImage}
                    defaultImage={profileImage?.url ? profileImage : defaultProfile}
                />
            </div>
            <div className="img-section">
                <h3>Color</h3>
                <ColorPicker updateTemplate={updateTemplate} template={template} />
            </div>
            <div className="img-section">
                <h3>Banner</h3>
                <Toggle active={images.find((x) => x.type === "banner")?.active ?? true} callback={toggleBanner} />
                <ImageUpload
                    type="banner"
                    disabled={!images.find((x) => x.type === "banner")?.active ?? false}
                    callback={updateTemplateImage}
                    defaultImage={bannerImage?.url ? bannerImage : defaultBanner}
                />
            </div>
        </div>
    )
}
